/**
 * Helpers for detecting duplicate non-maintenance pool services.
 *
 * Rule (2026-06-08): a SERVICE (job_line_items `type === 'service'`) is flaggable
 * when the SAME service repeats AND its catalog `service_type` is anything other
 * than `maintenance`. `maintenance` is the normal recurring weekly service and
 * must NEVER be flagged. Chemicals/extras (other line `type`s) are out of scope.
 *
 * Service identity for grouping: `serviceId ?? name` — the same SKU repeated.
 *
 * Examples:
 *   two opening lines (same or different serviceIds) → keyed by serviceId; flag
 *   two closing lines same serviceId → flag
 *   two one_time lines same serviceId → flag
 *   two chemical-typed service lines same serviceId → flag
 *   two maintenance lines → NEVER flagged
 *   null/unknown serviceType → NEVER flagged
 */

/** @type {Set<string>} Service types that legitimately repeat — exempt from duplicate flagging. */
export const RECURRING_SERVICE_TYPES = new Set(['maintenance']);

/**
 * Returns true when serviceType legitimately recurs (should never be flagged).
 * @param {string|null|undefined} serviceType
 * @returns {boolean}
 */
export function isRecurringServiceType(serviceType) {
  return RECURRING_SERVICE_TYPES.has(serviceType);
}

/**
 * Groups entries by service identity and returns only groups with 2+ members
 * (i.e. actual duplicates). Considers ONLY entries where `serviceType` is
 * truthy AND `!isRecurringServiceType(serviceType)`.
 *
 * Service identity key: `serviceId ?? name` (stable first-seen order within
 * a group). Null/unknown serviceType entries are silently skipped.
 *
 * @param {Array<{ serviceType?: string|null, serviceId?: string|null, name?: string|null, ref?: * }>} entries
 * @returns {Array<{ key: string, serviceType: string, entries: Array<{ serviceType, serviceId, name, ref }> }>}
 */
export function findRepeatedServiceGroups(entries) {
  if (!Array.isArray(entries) || entries.length === 0) return [];

  /** @type {Map<string, { key: string, serviceType: string, entries: Array<*> }>} */
  const groups = new Map();

  for (const entry of entries) {
    if (!entry || typeof entry !== 'object') continue;
    const { serviceType, serviceId, name } = entry;

    // Skip: unknown/null type, or a legitimately recurring type (maintenance).
    if (!serviceType) continue;
    if (isRecurringServiceType(serviceType)) continue;

    // Group key = service identity: prefer serviceId, fall back to name.
    const key = serviceId ?? name ?? '';

    if (!groups.has(key)) {
      groups.set(key, { key, serviceType, entries: [] });
    }
    groups.get(key).entries.push(entry);
  }

  const result = [];
  for (const group of groups.values()) {
    if (group.entries.length >= 2) result.push(group);
  }
  return result;
}

/**
 * Pure derivation: given a lifecycle bundle's job array and a catalog lookup,
 * determine which jobs participate in a duplicate non-maintenance service group.
 *
 * Use case: warn the user before activating a lifecycle transition when the
 * period bundle contains repeated non-maintenance service lines — signs of
 * a data-entry mistake.
 *
 * The ref stored on each service entry is the jobId, so duplicate groups let
 * us identify which specific jobs are involved.
 *
 * @param {Array<{
 *   id: string,
 *   lines: Array<{ catalog_id?: string|null, name?: string|null }>,
 * }>} jobs - the `jobs` array from `fetchLifecyclePeriodBundle`
 * @param {Map<string, { service_type?: string|null, name?: string|null }> | object} servicesById
 *   Catalog/service lookup keyed by id string. Accepts Map or plain object.
 *   Typically built from the catalog items already in memory on the calling page.
 * @returns {{
 *   hasDuplicates: boolean,
 *   flaggedJobIds: string[],       // deduped; jobs that appear in any duplicate group
 *   duplicateServiceNames: string[], // deduped display name per duplicate group
 * }}
 */
export function derivePeriodDuplicates(jobs, servicesById) {
  const empty = { hasDuplicates: false, flaggedJobIds: [], duplicateServiceNames: [] };

  if (!Array.isArray(jobs) || jobs.length === 0) return empty;
  if (!servicesById) return empty;

  // Accumulate entries across ALL jobs; ref = jobId so duplicate groups point back
  // to the specific job(s) responsible.
  const allEntries = [];
  for (const job of jobs) {
    if (!job || typeof job !== 'object') continue;
    const jobId = job.id != null ? String(job.id) : null;
    if (!jobId) continue;
    const lines = Array.isArray(job.lines) ? job.lines : [];
    const entries = buildServiceEntries(lines, servicesById, () => jobId);
    for (const e of entries) allEntries.push(e);
  }

  const groups = findRepeatedServiceGroups(allEntries);
  if (groups.length === 0) return empty;

  // Collect flagged job ids (dedupe) and display names (dedupe)
  const flaggedSet = new Set();
  const nameSet = new Set();

  for (const group of groups) {
    // Display name: use the first entry's name, or fall back to the group key
    const displayName = group.entries[0]?.name ?? group.key;
    nameSet.add(displayName);
    for (const entry of group.entries) {
      if (entry?.ref != null) flaggedSet.add(String(entry.ref));
    }
  }

  return {
    hasDuplicates: true,
    flaggedJobIds: Array.from(flaggedSet),
    duplicateServiceNames: Array.from(nameSet),
  };
}

/**
 * Builds a normalized entry array from raw line items + a services lookup.
 *
 * @param {Array<{ service_id?: *, catalog_id?: *, name?: string }>} lineItems
 * @param {Map<string, { service_type?: string|null, name?: string|null }> | object} servicesById
 *   Keyed by **string** service id. Accepts both Map and plain object.
 * @param {(lineItem: object) => *} [makeRef] Optional tagger — called with each
 *   raw line item and its return value is stored as `ref`. Defaults to `() => undefined`.
 * @returns {Array<{ serviceType: string|null, serviceId: string|null, name: string|null, ref: * }>}
 */
export function buildServiceEntries(lineItems, servicesById, makeRef) {
  if (!Array.isArray(lineItems) || !lineItems.length) return [];
  const tagFn = typeof makeRef === 'function' ? makeRef : () => undefined;

  // Accept both Map and plain object for servicesById
  const lookup = servicesById instanceof Map
    ? (id) => servicesById.get(id)
    : (id) => (servicesById && typeof servicesById === 'object' ? servicesById[id] : undefined);

  return lineItems
    .filter((item) => item && typeof item === 'object')
    .map((item) => {
      const id = String(item.service_id ?? item.catalog_id ?? '') || null;
      const catalogEntry = id ? lookup(id) : undefined;
      return {
        serviceType: catalogEntry?.service_type ?? null,
        serviceId: id,
        name: catalogEntry?.name ?? item.name ?? null,
        ref: tagFn(item),
      };
    });
}

// ─────────────────────────────────────────────────────────────────────────────
// derivePeriodDuplicateGroups — resolution-oriented view of the same data.
// Returns a structured list of duplicate groups, each with an occurrence per
// unique job, so the DuplicateResolverPanel can let the user pick a survivor
// and remove the rest.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Compute the service identity key for a raw line item.
 * Mirrors the logic inside `buildServiceEntries` so grouping is consistent.
 *
 * @param {{ service_id?: *, catalog_id?: *, name?: string }} line
 * @returns {string|null}
 */
function lineIdentityKey(line) {
  if (!line || typeof line !== 'object') return null;
  const id = String(line.service_id ?? line.catalog_id ?? '') || null;
  return id ?? (line.name ? String(line.name) : null);
}

/**
 * Resolution-oriented duplicate detector for the lifecycle activation review gate.
 *
 * Returns one entry per duplicate group (≥2 job occurrences), with enough
 * detail for the DuplicateResolverPanel to render a pick-one UI. Does NOT
 * modify or replace `derivePeriodDuplicates` — that function drives the
 * banner/bulk scan; this one drives the resolver.
 *
 * @param {Array<{
 *   id: string,
 *   date: string,
 *   total?: number,
 *   lines: Array<{ type?: string, service_id?: *, catalog_id?: *, name?: string }>,
 * }>} jobs - the bundle's job array
 * @param {Map<string, { service_type?: string|null, name?: string|null }> | object} servicesById
 *   Catalog/service lookup keyed by string id.
 * @returns {Array<{
 *   key: string,
 *   serviceName: string,
 *   catalogId: string|null,
 *   occurrences: Array<{
 *     jobId: string,
 *     date: string,
 *     total: number,
 *     primaryName: string,
 *     isPureDuplicate: boolean,
 *   }>,
 * }>}
 */
export function derivePeriodDuplicateGroups(jobs, servicesById) {
  if (!Array.isArray(jobs) || jobs.length === 0) return [];
  if (!servicesById) return [];

  // Build entries across all jobs, using jobId as the ref (same as derivePeriodDuplicates).
  const allEntries = [];
  for (const job of jobs) {
    if (!job || typeof job !== 'object') continue;
    const jobId = job.id != null ? String(job.id) : null;
    if (!jobId) continue;
    const lines = Array.isArray(job.lines) ? job.lines : [];
    const entries = buildServiceEntries(lines, servicesById, () => jobId);
    for (const e of entries) allEntries.push(e);
  }

  const groups = findRepeatedServiceGroups(allEntries);
  if (groups.length === 0) return [];

  // Build a Set of all keys that appear in a duplicate group (for isPureDuplicate check).
  const dupKeySet = new Set(groups.map((g) => g.key));

  // Build a jobId→job lookup for efficient access.
  const jobById = new Map();
  for (const job of jobs) {
    if (job && job.id != null) jobById.set(String(job.id), job);
  }

  return groups.map((group) => {
    // Collect unique jobIds that have this duplicate service.
    const seenJobIds = new Set();
    for (const entry of group.entries) {
      if (entry?.ref != null) seenJobIds.add(String(entry.ref));
    }

    // Build occurrences sorted by date ascending (earliest = default survivor).
    const occurrences = Array.from(seenJobIds)
      .map((jobId) => {
        const job = jobById.get(jobId);
        if (!job) return null;
        const lines = Array.isArray(job.lines) ? job.lines : [];

        // isPureDuplicate: every line is either type=service AND its identity key
        // is in dupKeySet (the flagged service), or the job has no lines.
        // A job with zero lines → false (nothing to keep).
        const isPureDuplicate = lines.length > 0 && lines.every((line) => {
          if (!line || typeof line !== 'object') return false;
          if (line.type !== 'service') return false;
          const key = lineIdentityKey(line);
          return key !== null && dupKeySet.has(key);
        });

        return {
          jobId,
          date: job.date ?? '',
          total: job.total ?? 0,
          primaryName: lines[0]?.name ?? '—',
          isPureDuplicate,
        };
      })
      .filter(Boolean)
      .sort((a, b) => {
        if (a.date < b.date) return -1;
        if (a.date > b.date) return 1;
        return 0;
      });

    const serviceName = group.entries[0]?.name ?? group.key;

    // catalogId: the catalog id when the group is keyed by a real catalog id,
    // else null (name-only keyed groups have no catalog_id to filter on).
    const firstServiceId = group.entries[0]?.serviceId ?? null;
    // The key equals serviceId only when serviceId was used as the key.
    // If the key is the same as firstServiceId it came from catalog_id/service_id.
    const catalogId = firstServiceId && firstServiceId === group.key ? firstServiceId : null;

    return { key: group.key, serviceName, catalogId, occurrences };
  });
}
